// Grants editor of the user form: one row per grant (étage · service · niveau), an "add" button
// and the plain-French legend of the levels (SPEC §7, §8).
import { h, button, iconButton, select, classNames } from '../../dom.js';
import { t } from '../../i18n/fr.js';
import { sortedFloors } from '../../store.js';
import { GRANT_LEVELS, cleanGrants, defaultGrant, deptOptions, levelLabel, validateGrants } from './model.js';
import { aid } from './common.js';
import './strings.js';

/** The legend of the access levels (a definition list). */
export function levelLegend() {
  const row = (level, text) => h('div', { class: 'adm-legend-row' },
    h('dt', {}, h('span', { class: classNames(['badge', 'badge-neutral', `adm-level-${level}`]) }, levelLabel(level))),
    h('dd', {}, text));
  return h('details', { class: 'adm-legend' },
    h('summary', { class: 'adm-legend-summary' }, t('adm.legend.title')),
    h('dl', { class: 'adm-legend-list' },
      row('view', t('adm.legend.view')),
      row('operate', t('adm.legend.operate')),
      row('manage', t('adm.legend.manage'))),
    h('p', { class: 'adm-legend-admin muted' }, t('adm.legend.admin')));
}

/**
 * Creates the editor.
 * @param {{office: object, grants?: object[], disabled?: boolean, onChange?: Function}} opts
 * @returns {{el: HTMLElement, getValue(): object[], validate(): string[], showProblems(p: string[]): void, setDisabled(d: boolean): void}}
 */
export function createGrantsEditor({ office, grants = [], disabled = false, onChange } = {}) {
  let rows = cleanGrants(grants).map((g) => ({ ...g, key: aid('g') }));
  let isDisabled = disabled;
  const floors = sortedFloors(office?.floors || []);
  const list = h('div', { class: 'adm-grants-list', role: 'list' });
  const problemsEl = h('ul', { class: 'adm-grants-problems', role: 'alert', hidden: true });
  const addBtn = button(t('adm.grant.add'), { variant: 'secondary', size: 'sm', icon: 'plus', onClick: () => add() });
  const el = h('div', { class: 'adm-grants' }, list, problemsEl,
    h('div', { class: 'adm-grants-foot' }, addBtn), levelLegend());

  function changed() {
    onChange?.(getValue());
  }

  function add() {
    const g = defaultGrant(office);
    rows = [...rows, { ...g, key: aid('g') }];
    render(rows[rows.length - 1].key);
    changed();
  }

  function remove(key) {
    const idx = rows.findIndex((r) => r.key === key);
    rows = rows.filter((r) => r.key !== key);
    render(null, idx);
    changed();
  }

  function rowEl(r, i) {
    const floorOpts = floors.map((f) => ({ value: f.id, label: f.name }));
    if (r.floorId && !floors.some((f) => f.id === r.floorId)) floorOpts.unshift({ value: r.floorId, label: t('adm.grants.unknownFloor', { id: r.floorId }) });
    if (!r.floorId) floorOpts.unshift({ value: '', label: '—' });
    const floorSel = select(floorOpts, { value: r.floorId, class: 'input-sm', disabled: isDisabled, 'aria-label': t('adm.grant.floor') });
    const dOpts = deptOptions(office, r.floorId);
    if (r.deptId !== '*' && !dOpts.some((o) => o.value === r.deptId)) dOpts.push({ value: r.deptId, label: t('adm.grants.unknownDept', { id: r.deptId }) });
    const deptSel = select(dOpts, { value: r.deptId, class: 'input-sm', disabled: isDisabled, 'aria-label': t('adm.grant.dept') });
    const levelSel = select(GRANT_LEVELS.map((l) => ({ value: l, label: levelLabel(l) })),
      { value: r.level, class: 'input-sm', disabled: isDisabled, 'aria-label': t('adm.grant.level') });
    floorSel.addEventListener('change', () => {
      r.floorId = floorSel.value;
      r.deptId = '*';
      render(`${r.key}:floor`);
      changed();
    });
    deptSel.addEventListener('change', () => { r.deptId = deptSel.value; changed(); });
    levelSel.addEventListener('change', () => { r.level = levelSel.value; changed(); });
    const del = iconButton('close', t('adm.grant.remove'), { variant: 'ghost', size: 16, disabled: isDisabled, onClick: () => remove(r.key) });
    return h('div', { class: 'adm-grant', role: 'listitem', 'aria-label': t('adm.grant.rowAria', { n: i + 1 }) },
      h('label', { class: 'adm-grant-cell' }, h('span', { class: 'adm-grant-label mono-label' }, t('adm.grant.floor')), floorSel),
      h('label', { class: 'adm-grant-cell' }, h('span', { class: 'adm-grant-label mono-label' }, t('adm.grant.dept')), deptSel),
      h('label', { class: 'adm-grant-cell' }, h('span', { class: 'adm-grant-label mono-label' }, t('adm.grant.level')), levelSel),
      del);
  }

  /** Re-renders the rows; focuses the control with key `focusKey`, or the row near `removedIdx`. */
  function render(focusKey = null, removedIdx = -1) {
    list.replaceChildren();
    if (!floors.length) {
      list.appendChild(h('p', { class: 'adm-grants-empty muted' }, t('adm.grant.noFloors')));
    } else if (!rows.length) {
      list.appendChild(h('p', { class: 'adm-grants-empty muted' }, t('adm.grant.empty')));
    }
    const els = rows.map((r, i) => rowEl(r, i));
    for (const e of els) list.appendChild(e);
    addBtn.disabled = isDisabled || !floors.length;
    let target = null;
    if (focusKey) {
      // "<rowKey>" or "<rowKey>:floor": both focus the row's first select (the floor).
      const key = String(focusKey).split(':')[0];
      const idx = rows.findIndex((r) => r.key === key);
      if (idx >= 0) target = els[idx].querySelector('select');
    } else if (removedIdx >= 0) {
      const idx = Math.min(removedIdx, rows.length - 1);
      target = idx >= 0 ? els[idx].querySelector('button') : addBtn;
    }
    target?.focus?.();
  }

  function getValue() {
    return cleanGrants(rows);
  }

  render();
  return {
    el,
    getValue,
    validate: () => validateGrants(getValue(), office),
    showProblems(problems) {
      problemsEl.replaceChildren(...(problems || []).map((p) => h('li', {}, p)));
      problemsEl.hidden = !(problems && problems.length);
    },
    setDisabled(d) {
      isDisabled = !!d;
      render();
    },
  };
}
